import {
  QUOTE_PRICE_FIELDS,
  type QuotePriceField,
} from '@ui/config/price/constants';

import { getQuoteLineOrThrow } from '../../utilities/action-guards';
import { handlePartBaseGrossMarginChange } from './base-gross-margin';
import { handlePartContributionMarginChange } from './contribution-margin';
import { handlePartGrossMarginChange } from './gross-margin';
import { handlePartLeadTimeFieldChange } from './lead-time';
import { handleNoOp } from './no-op';
import {
  handlePartQuantityFieldChange,
  handleServiceQuantityFieldChange,
} from './quantity';
import { PriceFieldHandlerParams } from './types';
import { handlePartUnitCostChange } from './unit-cost';
import {
  handlePartUnitPriceChange,
  handleServiceUnitPriceChange,
} from './unit-price';

type Params = PriceFieldHandlerParams & {
  field: QuotePriceField;
};

const PART_FIELD_HANDLERS: Record<
  QuotePriceField,
  (params: PriceFieldHandlerParams) => void
> = {
  [QUOTE_PRICE_FIELDS.quantity]: handlePartQuantityFieldChange,
  [QUOTE_PRICE_FIELDS.unitPrice]: handlePartUnitPriceChange,
  [QUOTE_PRICE_FIELDS.unitCost]: handlePartUnitCostChange,
  [QUOTE_PRICE_FIELDS.grossMargin]: handlePartGrossMarginChange,
  [QUOTE_PRICE_FIELDS.contributionMargin]: handlePartContributionMarginChange,
  [QUOTE_PRICE_FIELDS.baseGrossMargin]: handlePartBaseGrossMarginChange,
  [QUOTE_PRICE_FIELDS.unitVariableCosts]: handleNoOp,
  [QUOTE_PRICE_FIELDS.priceAdjustmentAmount]: handleNoOp,
  [QUOTE_PRICE_FIELDS.leadTimeWeeks]: handlePartLeadTimeFieldChange,
};

const SERVICE_CHARGE_FIELD_HANDLERS: Record<
  QuotePriceField,
  (params: PriceFieldHandlerParams) => void
> = {
  // @NOTE(mrs): At this time, service charges only have Price and Quantity; all other fields
  // are not relevant, so cannot be updated / are a NoOp
  [QUOTE_PRICE_FIELDS.quantity]: handleServiceQuantityFieldChange,
  [QUOTE_PRICE_FIELDS.unitPrice]: handleServiceUnitPriceChange,
  [QUOTE_PRICE_FIELDS.unitCost]: handleNoOp,
  [QUOTE_PRICE_FIELDS.grossMargin]: handleNoOp,
  [QUOTE_PRICE_FIELDS.contributionMargin]: handleNoOp,
  [QUOTE_PRICE_FIELDS.baseGrossMargin]: handleNoOp,
  [QUOTE_PRICE_FIELDS.unitVariableCosts]: handleNoOp,
  [QUOTE_PRICE_FIELDS.priceAdjustmentAmount]: handleNoOp,
  [QUOTE_PRICE_FIELDS.leadTimeWeeks]: handleNoOp,
};

export function handlePriceFieldChange(params: Params) {
  const { field, ...rest } = params;

  const quoteLine = getQuoteLineOrThrow(params.state, params.quoteLineUuid);

  if (quoteLine.serviceChargeUuid) {
    const handler = SERVICE_CHARGE_FIELD_HANDLERS[field];

    handler(rest);

    return;
  }

  const handler = PART_FIELD_HANDLERS[field];

  handler(rest);
}
